import { useState } from "react";
import type { ExpertSummary } from "../api";
import { Card } from "../components/Card";
import { Eyebrow } from "../components/Eyebrow";
import { ExpertMark } from "../components/design/ExpertMark";
import { Tag } from "../components/design/Tag";
import { VoiceSignature } from "../components/design/VoiceSignature";
import { LangPill } from "../components/design/LangPill";
import { Segmented } from "../components/design/Segmented";
import { StatusDot } from "../components/design/StatusDot";

// Static fixtures for the gallery. Keeping /design API-free per the
// web-ui spec ("no API calls to /v1/* endpoints"); this means the route
// works for an unauthenticated visitor and renders deterministically.
const EXPERT_FIXTURES: Array<
  Pick<
    ExpertSummary,
    "id" | "version" | "display_name" | "monogram" | "color_token" | "voice_id" | "voice_desc"
  >
> = [
  {
    id: "software-spec-author",
    version: 1,
    display_name: "Software Spec Author",
    monogram: "SP",
    color_token: "spec",
    voice_id: "warm-baritone",
    voice_desc: "Warm baritone · measured pace",
  },
  {
    id: "aws-solutions-architect",
    version: 1,
    display_name: "AWS Solutions Architect",
    monogram: "AS",
    color_token: "aws",
    voice_id: "crisp-tenor",
    voice_desc: "Crisp tenor · slight Pacific NW",
  },
  {
    id: "system-design-reviewer",
    version: 1,
    display_name: "System Design Reviewer",
    monogram: "SD",
    color_token: "sys",
    voice_id: "calm-alto",
    voice_desc: "Calm alto · careful enunciation",
  },
  {
    id: "privacy-counsel",
    version: 1,
    display_name: "Privacy Counsel",
    monogram: "GD",
    color_token: "gdpr",
    voice_id: "deliberate-mezzo",
    voice_desc: "Deliberate mezzo · British vowels",
  },
  {
    id: "brazilian-contador",
    version: 1,
    display_name: "Contador Brasileiro",
    monogram: "CT",
    color_token: "ctd",
    voice_id: "soft-baritone",
    voice_desc: "Barítono suave · sotaque paulista",
  },
  {
    id: "business-strategist",
    version: 1,
    display_name: "Business Strategist",
    monogram: "ST",
    color_token: "str",
    voice_id: "buoyant-tenor",
    voice_desc: "Buoyant tenor · brisk pace",
  },
];

const TOKEN_GROUPS: Array<{ heading: string; tokens: string[] }> = [
  {
    heading: "Surfaces",
    tokens: ["--paper", "--paper-2", "--paper-3", "--card", "--tape"],
  },
  {
    heading: "Ink",
    tokens: ["--ink", "--ink-2", "--ink-3", "--ink-4"],
  },
  {
    heading: "Hairlines",
    tokens: ["--rule", "--rule-2"],
  },
  {
    heading: "Status",
    tokens: ["--ok", "--warn", "--err", "--live"],
  },
  {
    heading: "Experts",
    tokens: ["--spec", "--aws", "--sys", "--gdpr", "--ctd", "--str"],
  },
];

export function Design() {
  const experts = EXPERT_FIXTURES;

  return (
    <div
      style={{
        background: "var(--paper)",
        color: "var(--ink)",
        minHeight: "100vh",
        font: "var(--type-body)",
        padding: "var(--space-7)",
      }}
    >
      <div style={{ maxWidth: 960, margin: "0 auto" }}>
        <Eyebrow>RecSpec · Design system</Eyebrow>
        <h1
          style={{
            font: "var(--type-display)",
            margin: "var(--space-2) 0 var(--space-6)",
          }}
        >
          tokens, type, primitives
        </h1>

        <section style={{ marginBottom: "var(--space-7)" }}>
          <Eyebrow>Tokens</Eyebrow>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: "var(--space-3)",
              marginTop: "var(--space-3)",
            }}
          >
            {TOKEN_GROUPS.flatMap((g) =>
              g.tokens.map((t) => (
                <Card key={t} style={{ padding: "var(--space-3)" }}>
                  <div
                    style={{
                      height: 48,
                      background: `var(${t})`,
                      border: "1px solid var(--rule)",
                      borderRadius: "var(--radius-md)",
                      marginBottom: "var(--space-2)",
                    }}
                  />
                  <div style={{ font: "var(--type-mono)" }}>{t}</div>
                  <div style={{ font: "var(--type-eyebrow)", color: "var(--muted)" }}>
                    {g.heading}
                  </div>
                </Card>
              )),
            )}
          </div>
        </section>

        <section style={{ marginBottom: "var(--space-7)" }}>
          <Eyebrow>Type stack</Eyebrow>
          <div style={{ marginTop: "var(--space-3)", display: "grid", gap: "var(--space-3)" }}>
            <div style={{ font: "var(--type-display)" }}>Display · Instrument Serif</div>
            <div style={{ font: "var(--type-h1)" }}>Heading 1 · Geist 32</div>
            <div style={{ font: "var(--type-h2)" }}>Heading 2 · Geist 22</div>
            <div style={{ font: "var(--type-body)" }}>
              Body · Geist 16/1.55 — the bulk of the SPA reads in this register.
            </div>
            <div style={{ font: "var(--type-mono)" }}>
              Mono · Geist Mono 13 — used for ids, code, monogram, and eyebrow.
            </div>
          </div>
        </section>

        <section style={{ marginBottom: "var(--space-7)" }}>
          <Eyebrow>Experts</Eyebrow>
          <div
            style={{
              marginTop: "var(--space-3)",
              display: "grid",
              gap: "var(--space-3)",
            }}
          >
            {experts.map((e) => (
              <Card key={e.id}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--space-4)",
                  }}
                >
                  <ExpertMark expert={e} size={56} />
                  <div style={{ flex: 1 }}>
                    <div style={{ font: "var(--type-h2)" }}>{e.display_name}</div>
                    <div style={{ font: "var(--type-body-sm)", color: "var(--muted)" }}>
                      {e.voice_desc}
                    </div>
                  </div>
                  <VoiceSignature expert={e} />
                  <VoiceSignature expert={e} active />
                </div>
                <div
                  style={{
                    marginTop: "var(--space-3)",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "var(--space-2)",
                  }}
                >
                  <Tag color={e.color_token}>{e.color_token}</Tag>
                  <Tag>{e.id}</Tag>
                  <Tag>v{e.version}</Tag>
                </div>
              </Card>
            ))}
          </div>
        </section>

        <section>
          <Eyebrow>Primitives</Eyebrow>
          <div
            style={{
              marginTop: "var(--space-3)",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "var(--space-3)",
            }}
          >
            <Card>
              <Eyebrow>Card · default</Eyebrow>
              <div style={{ marginTop: "var(--space-2)" }}>
                Surface card on `--paper`.
              </div>
            </Card>
            <Card raised>
              <Eyebrow>Card · raised</Eyebrow>
              <div style={{ marginTop: "var(--space-2)" }}>
                Lifted to `--paper-raised`.
              </div>
            </Card>
            <Card>
              <Eyebrow>Tags (color)</Eyebrow>
              <div
                style={{
                  marginTop: "var(--space-2)",
                  display: "flex",
                  gap: "var(--space-2)",
                  flexWrap: "wrap",
                }}
              >
                <Tag>Neutral</Tag>
                <Tag color="spec">Spec</Tag>
                <Tag color="aws">AWS</Tag>
                <Tag color="sys">Sys</Tag>
                <Tag color="ok" dot>
                  OK
                </Tag>
                <Tag color="err" dot>
                  Err
                </Tag>
              </div>
            </Card>
            <Card>
              <Eyebrow>LangPill</Eyebrow>
              <div
                style={{
                  marginTop: "var(--space-2)",
                  display: "flex",
                  gap: 4,
                }}
              >
                <LangPill id="EN" active />
                <LangPill id="PT" />
                <LangPill id="DE" supported={false} />
              </div>
            </Card>
            <Card>
              <Eyebrow>StatusDot</Eyebrow>
              <div
                style={{
                  marginTop: "var(--space-2)",
                  display: "flex",
                  gap: "var(--space-2)",
                  alignItems: "center",
                }}
              >
                <StatusDot color="var(--ok)" />
                <StatusDot color="var(--warn)" />
                <StatusDot color="var(--err)" />
                <StatusDot color="var(--live)" pulse />
              </div>
            </Card>
            <Card>
              <SegmentedDemo />
            </Card>
          </div>
        </section>

        <section style={{ marginTop: "var(--space-7)" }}>
          <Eyebrow>Status colors</Eyebrow>
          <div
            style={{
              marginTop: "var(--space-3)",
              display: "flex",
              gap: "var(--space-4)",
              flexWrap: "wrap",
            }}
          >
            {[
              { name: "--ok", label: "OK" },
              { name: "--warn", label: "Warn" },
              { name: "--err", label: "Err" },
              { name: "--live", label: "Live" },
            ].map((s) => (
              <div
                key={s.name}
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                <span
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: 4,
                    background: `var(${s.name})`,
                    border: "1px solid var(--rule)",
                  }}
                />
                <span style={{ fontSize: 13 }}>{s.label}</span>
                <span className="mono dim" style={{ fontSize: 11 }}>
                  {s.name}
                </span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function SegmentedDemo() {
  const [v, setV] = useState<"a" | "b" | "c">("b");
  return (
    <>
      <Eyebrow>Segmented</Eyebrow>
      <div style={{ marginTop: "var(--space-2)" }}>
        <Segmented<"a" | "b" | "c">
          value={v}
          onChange={setV}
          size="sm"
          options={[
            { value: "a", label: "EN" },
            { value: "b", label: "PT" },
            { value: "c", label: "DE" },
          ]}
        />
      </div>
    </>
  );
}
